/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    getCurrentUserIsGuest,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { Clickable, GridContainer, GridItem, LazyImage, LoadingOverlay } from "@insite/mobius/index";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PetForm from "@rc/App/Components/MyPets/PetForm";
import getMyPetsData from "@rc/App/Handlers/MyPets/getMyPets";
import { CustomAppState } from "@rc/App/Store/Reducers";
import { PetType } from "@rc/App/Store/Reducers/xMyPets";
import Button from "@rc/MobiusOverrides/Button/Button";
import Modal from "@rc/MobiusOverrides/Modal/Modal";
import { xScreen } from "@rc/XDL/Utilities";
import React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {}

interface OwnProps extends WidgetProps {
    fields: {};
}

const mapStateToProps = (state: CustomAppState) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;
    const userPets = state.xMyPets.userPets;
    const session = getSession(state);
    const catImage = (getSettingsCollection(state).customSetting as any)?.rcCatImage;
    const dogImage = (getSettingsCollection(state).customSetting as any)?.rcDogImage;

    return {
        isAuthenticated: userName && !currentUserIsGuest,
        userPets,
        subscriberKey: session?.properties?.subscriberKey || "",
        petDetailsPageUrl: getPageLinkByPageType(state, "MyPetDetailsPage")?.url,
        addPetPageUrl: getPageLinkByPageType(state, "MyPetAddPetPage")?.url,
        catImage,
        dogImage,
    };
};

const mapDispatchToProps = {
    getMyPetsData,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface MyPetsStyles {
    container?: InjectableCss;
    actionsContainer?: InjectableCss;
    itemContainer?: InjectableCss;
}

export const myPetsStyles: MyPetsStyles = {
    actionsContainer: {
        css: css`
            display: flex;
            justify-content: flex-end;
            align-items: center;
            margin-bottom: 32px;

            ${xScreen("lte", "xs")} {
                flex-direction: column;
                > button {
                    margin-right: 0;
                    width: 100%;
                    &:first-child {
                        margin-bottom: 10px;
                    }
                }
            }
        `,
    },
    itemContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center:
        `,
    },
};

const MyPets = ({
    isAuthenticated,
    userPets,
    getMyPetsData,
    subscriberKey,
    history,
    toaster,
    petDetailsPageUrl,
    addPetPageUrl,
    catImage,
    dogImage,
}: Props) => {
    const styles = myPetsStyles;
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        if (isAuthenticated) {
            setIsLoading(true);
            getMyPetsData({
                subscriberKey,
                onSuccess: (result: any) => {
                    setIsLoading(false);
                },
                onError: () => {
                    setIsLoading(false);
                },
            });
        }
    }, []);

    if (!isAuthenticated) {
        return null;
    }

    const addPet = (type: string) => {
        const isImpersonating = false;
        if (isImpersonating) {
            // impersonating
        } else {
            const url = `${addPetPageUrl}?species=${type}`;
            history.push(url);
        }
    };

    const goTo = (petID: string, species: string) => {
        const isImpersonating = false;
        if (isImpersonating) {
            // impersonating
        } else {
            if (!petDetailsPageUrl) {
                return;
            }
            const url = `${petDetailsPageUrl}?species=${species}&petKey=${petID}`;
            history.push(url);
        }
    };

    return (
        <StyledWrapper {...styles.container}>
            <LoadingOverlay loading={isLoading === true}>
                <StyledWrapper {...styles.actionsContainer}>
                    <Button
                        onClick={() => {
                            addPet("cat");
                        }}
                        css={css`
                            margin-right: 10px;
                        `}
                    >
                        {translate("Add a Cat")}
                    </Button>
                    <Button
                        onClick={() => {
                            addPet("dog");
                        }}
                    >
                        {translate("Add a Dog")}
                    </Button>
                </StyledWrapper>

                {userPets?.length === 0 && <Typography>{siteMessage("Custom_MyPets_NoPetsFound")}</Typography>}

                {userPets?.length > 0 && (
                    <GridContainer gap={20}>
                        {userPets.map((pet: PetType) => (
                            <GridItem width={[6, 4, 3, 2.4, 2.4] as any} key={pet.petID} css={css``}>
                                <Clickable
                                    onClick={() => {
                                        goTo(pet.petID, pet.petSpecies);
                                    }}
                                    css={css`
                                        width: 100%;
                                        height: 100%;
                                        border: 1px solid ${getColor("common.ui3")};
                                        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                                        display: block;

                                        img {
                                            width: 100%;
                                            height: 200px;
                                            max-height: 200px;
                                        }
                                    `}
                                >
                                    <StyledWrapper
                                        className={`${pet.petSpecies === "Cat" && "feline"} ${
                                            pet.petSpecies === "Dog" && "canine"
                                        } `}
                                        {...styles.itemContainer}
                                    >
                                        <LazyImage
                                            src={pet.petSpecies === "Cat" ? catImage : dogImage}
                                            altText={pet.petSpecies}
                                        ></LazyImage>
                                        <Typography
                                            variant="h6"
                                            css={css`
                                                padding: 8px;
                                                margin: 0;
                                            `}
                                        >
                                            {pet.petName}
                                        </Typography>
                                    </StyledWrapper>
                                </Clickable>
                            </GridItem>
                        ))}
                    </GridContainer>
                )}
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(MyPets))),
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [],
        allowedContexts: ["MyPetDetailsPage", "MyPetsPage"],
    },
};

export default widgetModule;
